import { globalHistory } from "@gatsbyjs/reach-router";
import React, { useCallback, useState, useEffect, useRef, useContext } from "react";
import { Box, Flex } from "rebass/styled-components";

import { corewellAccordionItemOverrideClass } from "../../../../constants/constants";
import { AppDataContext } from "../../../contexts/app-data/AppDataContext";
import { Icon, IconName, IconPack } from "../../../models/app-data-model";
import ShIcon from "../../sh-icon/sh-icon";
import "./accordion-item.scss";

export type AccordionItemProps = {
  heading: string;
  linkId?: string;
  headingClass?: string;
  headingIconName?: string;
  headingIconClass?: string;
  svgIcon?: Icon;
  open?: boolean;
  children?: React.ReactNode;
  iconPack: IconPack[];
  defaultIcon?: boolean;
  corewellStyleOverrides?: boolean;
  insideOfTab?: boolean;
};

const AccordionItem: React.FC<AccordionItemProps> = ({
  heading,
  linkId,
  headingClass,
  headingIconName,
  headingIconClass,
  open,
  children,
  iconPack,
  defaultIcon,
  corewellStyleOverrides,
  insideOfTab,
  svgIcon
}: AccordionItemProps) => {
  const [isOpen, setIsOpen] = useState(open);
  const [bodyHeight, setBodyHeight] = useState(0);
  const accordionBody = useRef(null);
  const extraMarginClass = !!useContext(AppDataContext).subsiteData?.subsite_nav ? "with-subsite-nav" : "";

  const checkBodyHeight = useCallback((): void => {
    const accordionBodyHeight = (accordionBody as any).current?.offsetHeight;
    if (accordionBodyHeight && accordionBodyHeight !== 0) {
      setBodyHeight(accordionBodyHeight);
    }
  }, [setBodyHeight, accordionBody, isOpen]);

  useEffect(() => {
    setIsOpen(open);
  }, [open]);

  useEffect(() => {
    // check height of body item on initial load. Delayed to ensure fully loaded
    const delayCheckBodyHeight = setTimeout(() => {
      checkBodyHeight();
    }, 300);
    return () => clearTimeout(delayCheckBodyHeight);
  }, [checkBodyHeight, isOpen]);

  /**
   * this useEffect ensures the item is scrolled back into view if it's pushed down after the page loads
   * due to another item higher on the page expanding by default (behavior observed in Safari & Firefox).
   **/
  useEffect(() => {
    const delayScrollCorrection = setTimeout(() => {
      if (linkId && globalHistory.location?.hash === `#${linkId}`) {
        setIsOpen(true);
        document.getElementById(linkId)?.scrollIntoView({ block: "start", behavior: "smooth" });
      }
    }, 500);
    return () => clearTimeout(delayScrollCorrection);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // check height of body item when window size changes
    window?.addEventListener("resize", checkBodyHeight);
    return () => window?.removeEventListener("resize", checkBodyHeight);
  }, [checkBodyHeight]);

  useEffect(() => {
    return globalHistory.listen(({ location }) => {
      // istanbul ignore else
      if (linkId && location?.hash === `#${linkId}`) {
        setIsOpen(true);
      }
    });
  });

  const handleOnKeyUp = useCallback(
    (event: React.KeyboardEvent): void => {
      if (event.key === "Enter" || event.keyCode === 13) {
        setIsOpen(!isOpen);
      }
    },
    [isOpen]
  );

  // Corewell specific typography overrides

  const corewellHeadingClassOverrides = corewellStyleOverrides ? `${corewellAccordionItemOverrideClass.accordionHeadingTextOverride}` : "";
  const headingTextClasses = `accordion-heading-text ${corewellHeadingClassOverrides}`;

  const corewellBodyClassOverrides = corewellStyleOverrides ? ` ${corewellAccordionItemOverrideClass.accordionBodyCopyOverride}` : "";
  const bodyCopyClasses = `accordion-body ${corewellBodyClassOverrides}`;

  return (
    <div
      id={linkId}
      className={`accordion-item scroll-margin-top ${extraMarginClass} ${isOpen ? "open" : "closed"} ${
        insideOfTab && "tabbed-content-outline"
      }`}
    >
      <Flex
        className={`accordion-heading ${headingClass}`}
        tabIndex={0}
        onClick={() => setIsOpen(!isOpen)}
        onKeyUp={(e: React.KeyboardEvent) => handleOnKeyUp(e)}
      >
        <span className={headingTextClasses}>
          {svgIcon && <ShIcon iconPack={iconPack} icon={svgIcon} className={headingIconClass} />}
          {!svgIcon && headingIconName && (
            <ShIcon iconPack={iconPack} iconName={headingIconName as IconName} className={headingIconClass} />
          )}
          {heading}
        </span>
        {defaultIcon ? (
          <ShIcon iconPack={iconPack} iconName={"caret_down"} className="accordion-heading-icon cursor-pointer" />
        ) : (
          <div className="icon-container">
            <ShIcon iconPack={iconPack} iconName={"close"} className="accordion-heading-icon cursor-pointer" />
          </div>
        )}
      </Flex>
      <div className="accordion-body-wrapper" style={{ height: `${isOpen ? bodyHeight : 0}px` }} data-testid={isOpen ? "Open" : ""}>
        {isOpen && <Box className={bodyCopyClasses} px={4} paddingBottom={3} ref={accordionBody}>
          {children}
        </Box>}
      </div>
    </div>
  );
};

export default AccordionItem;
